'use client';

import React from 'react';
import { cn } from '@/utils/cn';
import { TypographyVariant } from '@/design-tokens/typography';

/**
 * Available HTML element types for text content
 */
export type TextElement = 'p' | 'span' | 'div';

/**
 * Available semantic variants that map to typography design tokens
 * Focused on body text variants for paragraph content
 */
export type ParagraphVariant = Extract<
  TypographyVariant,
  'bodyLarge' | 'bodyMedium' | 'bodySmall' | 'label' | 'tag'
>;

/**
 * Available text colors matching the design system
 */
export type ParagraphColor =
  | 'primary'
  | 'secondary'
  | 'brand'
  | 'error'
  | 'success'
  | 'warning'
  | 'neutral-gray-600'
  | 'neutral-gray-700'
  | 'neutral-gray-800'
  | 'neutral-black'
  | 'neutral-white';

/**
 * Available font weights
 */
export type ParagraphWeight = 'regular' | 'medium' | 'bold';

/**
 * Available text alignment options
 */
export type ParagraphAlign = 'inherit' | 'left' | 'center' | 'right' | 'justify';

/**
 * Props for the TextParagraph component
 */
export interface TextParagraphProps extends React.HTMLAttributes<HTMLElement> {
  /** The HTML element to render */
  as?: TextElement;
  /** Typography variant for size and base styling */
  variant?: ParagraphVariant;
  /** Text color */
  color?: ParagraphColor;
  /** Font weight override */
  weight?: ParagraphWeight;
  /** Text alignment */
  align?: ParagraphAlign;
  /** Additional CSS classes */
  className?: string;
  /** Child content */
  children?: React.ReactNode;
  /** HTML id attribute */
  id?: string;
  /** Optional state to indicate content is being loaded */
  isLoading?: boolean;
  /** Additional CSS classes to apply when loading */
  loadingClassName?: string;
  /** Number of loading lines to display (for multi-line placeholders) */
  loadingLines?: number;
  /** Optional text string that renders as children */
  text?: string;
  /** ARIA label for accessibility */
  'aria-label'?: string;
  /** ARIA describedby for accessibility */
  'aria-describedby'?: string;
  /** HTML data-content attribute for testing and analytics */
  'data-content'?: string;
  /** HTML data-testid attribute for testing */
  'data-testid'?: string;
}

/**
 * Mapping of variants to their font family, size, weight, and line height
 * Only includes paragraph-appropriate variants
 */
const variantStyles: Record<ParagraphVariant, string> = {
  bodyLarge: 'font-text text-body-large font-regular leading-body-large',
  bodyMedium: 'font-text text-body font-regular leading-body',
  bodySmall: 'font-text text-body-small font-regular leading-body-small',
  label: 'font-text text-label font-medium leading-label',
  tag: 'font-text text-tag font-medium leading-tag',
};

/**
 * Mapping of colors to their corresponding CSS classes
 */
const colorStyles: Record<ParagraphColor, string> = {
  primary: 'text-semantic-text-primary',
  secondary: 'text-semantic-text-secondary',
  brand: 'text-brand-primary',
  error: 'text-system-error',
  success: 'text-system-success',
  warning: 'text-system-warning',
  'neutral-gray-600': 'text-neutral-gray-600',
  'neutral-gray-700': 'text-neutral-gray-700',
  'neutral-gray-800': 'text-neutral-gray-800',
  'neutral-black': 'text-neutral-black',
  'neutral-white': 'text-neutral-white',
};

/**
 * Mapping of font weights to their corresponding CSS classes
 */
const weightStyles: Record<ParagraphWeight, string> = {
  regular: 'font-regular',
  medium: 'font-medium',
  bold: 'font-bold',
};

/**
 * Mapping of text alignment options to their corresponding CSS classes
 */
const alignStyles: Record<ParagraphAlign, string> = {
  inherit: 'text-[inherit]',
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

/**
 * TextParagraph Component
 *
 * A flexible text component for body content, descriptions, and inline text.
 * Supports multiple HTML elements (p, span, div) with consistent typography.
 *
 * Features:
 * - Multiple HTML element types (p, span, div)
 * - Typography variants from design system
 * - Comprehensive color system
 * - Font weight and alignment control
 * - Accessible by default
 *
 * @example
 * ```tsx
 * <TextParagraph variant="bodyLarge" color="primary">
 *   This is a large body paragraph with primary color.
 * </TextParagraph>
 *
 * <TextParagraph as="span" variant="bodySmall" color="secondary" weight="medium">
 *   This is a small span with medium weight.
 * </TextParagraph>
 *
 * <TextParagraph as="div" variant="label" align="center">
 *   Centered label text in a div
 * </TextParagraph>
 * ```
 */
export const TextParagraph: React.FC<TextParagraphProps> = ({
  as = 'p',
  variant = 'bodyMedium',
  color = 'primary',
  weight,
  align = 'inherit',
  className,
  children,
  id,
  isLoading = false,
  loadingClassName,
  loadingLines = 1,
  text,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'data-content': dataContent,
  'data-testid': testId,
  ...rest
}) => {
  // Return null if no children (or text) provided
  if (!isLoading && !children && !text) {
    return null;
  }

  // Get base styles from variant
  const baseClasses = variantStyles[variant];
  const colorClass = colorStyles[color];
  const alignClass = alignStyles[align];

  // Handle weight override
  let finalClasses = baseClasses;
  if (weight) {
    // Remove existing font-weight class and apply new one
    const weightClass = weightStyles[weight];
    finalClasses = baseClasses?.replace(/font-(regular|medium|bold)/, weightClass);
  }

  // Combine all classes
  // We avoid using cn() for the base + color combination to prevent text-* conflicts
  const classes = [finalClasses, colorClass, alignClass, className]?.filter(Boolean)?.join(' ');

  // Create the appropriate element
  const Element = as;

  // Get the classes for the loading state
  const loadingClasses = cn(
    'animate-pulse bg-neutral-gray-300 w-80 h-4',
    {
      'h-7': variant === 'bodyLarge',
      'h-5': variant === 'bodyMedium',
      'h-4': variant === 'bodySmall' || variant === 'label' || variant === 'tag',
    },
    loadingClassName,
  );

  // return the element
  return (
    <>
      {isLoading ? (
        <div className={cn('w-full', className)}>
          {Array.from({ length: loadingLines }).map((_, index) => (
            <div key={index} className={loadingClasses} />
          ))}
        </div>
      ) : (
        <Element
          className={classes}
          id={id}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          data-content={dataContent}
          data-testid={testId}
          {...rest}
        >
          {text || children}
        </Element>
      )}
    </>
  );
};

/**
 * Pre-configured text components for common use cases
 */

/** Large body text for prominent descriptions */
export const TextParagraphBodyLarge = (props: Omit<TextParagraphProps, 'variant'>) => (
  <TextParagraph variant="bodyLarge" {...props} />
);

/** Medium body text (default size) for general content */
export const TextParagraphBodyMedium = (props: Omit<TextParagraphProps, 'variant'>) => (
  <TextParagraph variant="bodyMedium" {...props} />
);

/** Small body text for secondary information */
export const TextParagraphBodySmall = (props: Omit<TextParagraphProps, 'variant'>) => (
  <TextParagraph variant="bodySmall" {...props} />
);

/** Label text for form elements and descriptors */
export const TextParagraphLabel = (props: Omit<TextParagraphProps, 'variant'>) => (
  <TextParagraph variant="label" {...props} />
);

/** Tag text for small badges and indicators */
export const TextParagraphTag = (props: Omit<TextParagraphProps, 'variant'>) => (
  <TextParagraph variant="tag" {...props} />
);

/** Inline text using span element */
export const TextParagraphInlineText = (props: Omit<TextParagraphProps, 'as'>) => (
  <TextParagraph as="span" {...props} />
);

/** Block text using div element */
export const TextParagraphBlockText = (props: Omit<TextParagraphProps, 'as'>) => (
  <TextParagraph as="div" {...props} />
);

// Export the main component as default
TextParagraph.displayName = 'TextParagraph';

export default TextParagraph;
